import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";

export default function JournalArticle() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/articles/${id}`)
      .then((r) => setArticle(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div data-testid="article-notfound" className="pt-40 pb-20 px-6 md:px-12 text-center">
        <p className="font-overline text-silver-muted mb-4">— 404</p>
        <h1 className="font-display text-5xl text-silver">Article not found</h1>
        <Link to="/journal" className="btn-editorial mt-10 inline-flex">Back to Journal</Link>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="pt-40 pb-20 text-center">
        <p className="font-overline text-silver-muted">Loading…</p>
      </div>
    );
  }

  return (
    <article data-testid="article-page" className="pt-32 pb-20 px-6 md:px-12">
      <Seo
        title={article.title}
        description={article.excerpt}
        image={article.cover_url || undefined}
        path={`/journal/${id}`}
        type="article"
      />
      <div className="max-w-3xl mx-auto">
        <Link to="/journal" data-testid="article-back" className="font-overline text-silver-muted hover:text-silver transition-colors">
          ← Journal
        </Link>
        <p className="mt-10 font-overline text-champagne">{article.publication || "Editorial"}</p>
        <h1 className="mt-6 font-display text-4xl md:text-6xl text-silver leading-[1.02] tracking-tighter">
          {article.title}
        </h1>
        <p className="mt-8 font-body text-xl text-silver-muted leading-relaxed border-l hairline-strong pl-6">
          {article.excerpt}
        </p>
      </div>

      {article.cover_url && (
        <div className="max-w-5xl mx-auto mt-16 aspect-[16/9] border hairline overflow-hidden relative">
          <img src={article.cover_url} alt={article.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 grain" />
        </div>
      )}

      <div className="max-w-3xl mx-auto mt-16 prose-luxe">
        {(article.body || "").split("\n").filter(Boolean).map((p, i) => (
          <p key={i} className="font-body text-silver-muted text-lg leading-[1.85]">{p}</p>
        ))}
      </div>

      {article.external_url && (
        <div className="max-w-3xl mx-auto mt-16">
          <a href={article.external_url} target="_blank" rel="noreferrer" className="btn-editorial">
            Read on {article.publication || "Source"} ↗
          </a>
        </div>
      )}

      <div className="max-w-3xl mx-auto mt-24 border-t hairline pt-12 flex justify-between">
        <Link to="/journal" className="btn-editorial">← All Articles</Link>
        <Link to="/contact" className="btn-editorial btn-editorial-filled">Press Inquiry</Link>
      </div>
    </article>
  );
}
